"use client";

/**
 * Cookie Consent Banner
 *
 * GDPR-compliant consent banner for browser observability (telemetry).
 * - Requires explicit user consent before initializing Faro SDK
 * - Persists consent choice in localStorage
 * - Provides opt-out mechanism
 *
 * @module components/cookie-consent-banner
 */

import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import {
  clearTelemetryConsent,
  getTelemetryConsentStatus,
  setTelemetryConsentStatus,
  type TelemetryConsentStatus,
} from "@/lib/telemetry-consent";

/**
 * Cookie Consent Banner Component
 *
 * Shows consent banner if user hasn't made a choice yet.
 * Initializes Faro SDK only after user accepts.
 *
 * @example
 * ```tsx
 * // In app/layout.tsx
 * <CookieConsentBanner />
 * ```
 */
export function CookieConsentBanner() {
  const t = useTranslations("app.identity.consent");
  const [consent, setConsent] = useState<TelemetryConsentStatus>("pending");
  const [mounted, setMounted] = useState(false);

  // Hydration fix - only render after mount
  useEffect(() => {
    setConsent(getTelemetryConsentStatus());
    setMounted(true);
  }, []);

  const handleAccept = () => {
    setTelemetryConsentStatus("accepted");
    setConsent("accepted");

    // Initialize Faro SDK after consent
    import("@yeti/telemetry/browser")
      .then(({ initFaro }) => {
        initFaro();
        console.info("✅ Telemetry enabled after user consent");
      })
      .catch((error) => {
        console.warn("⚠️ Failed to initialize telemetry:", error);
      });
  };

  const handleReject = () => {
    setTelemetryConsentStatus("rejected");
    setConsent("rejected");
    console.info("🚫 Telemetry disabled by user");
  };

  // Don't render on server (SSR) or if consent already given
  if (!mounted || consent !== "pending") return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[9] p-3 md:left-[var(--sidebar-width,0px)]"
      role="alertdialog"
      aria-labelledby="consent-title"
      aria-describedby="consent-description"
      data-testid="telemetry-consent-banner"
    >
      <div className="pointer-events-auto mx-auto max-w-5xl rounded-lg border border-gray-700 bg-gray-900 p-4 text-white shadow-lg">
        <div className="flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center">
          <div className="min-w-0 flex-1">
            <h3 id="consent-title" className="mb-2 text-lg font-semibold">
              {t("title")}
            </h3>
            <p id="consent-description" className="text-sm text-gray-300">
              {t("body")}
            </p>
            <p className="mt-2 text-xs text-gray-400">{t("details")}</p>
          </div>

          <div className="flex shrink-0 gap-3">
            <button
              onClick={handleReject}
              className="rounded-md bg-gray-700 px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-600"
              aria-label={t("rejectLabel")}
              type="button"
            >
              {t("reject")}
            </button>
            <button
              onClick={handleAccept}
              className="rounded-md bg-blue-600 px-6 py-2 text-sm font-medium transition-colors hover:bg-blue-500"
              aria-label={t("acceptLabel")}
              type="button"
            >
              {t("accept")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Check if user has given consent for telemetry
 *
 * Use this to conditionally initialize Faro in ObservabilityProvider
 *
 * @returns true if user accepted, false if rejected or pending
 */
export function hasConsentForTelemetry(): boolean {
  return getTelemetryConsentStatus() === "accepted";
}

/**
 * Clear consent (for testing or privacy settings)
 *
 * @example
 * ```tsx
 * // In privacy settings page
 * <button onClick={clearConsent}>Reset Consent</button>
 * ```
 */
export function clearConsent(): void {
  clearTelemetryConsent();
  window.location.reload(); // Reload to show banner again
}
